import { omit } from 'lodash-es';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type { GlobalTicketConditionsResponse } from '../../@types';
import { BETSLIP_TICKET_TYPE } from '../../constants';
import betslipMessageParser, { MessageBetType, OddsChangeStatus } from '../../helpers/betslipMessageParser';
import type { MessageType, WebsocketData } from '../../hooks/useWebsocket';
import type { BetBuilderCalculateResponse, BetBuilderSelection } from '../../types/betBuilder';
import type { BetslipEvent, ErrorMessageOddsChangePayload } from '../../types/betslip';
import { WALLET_TYPE, WalletType } from '../../types/enums';
import {
  applyBetBuilderResponseToBets,
  areSameEventSelectionsCurrent,
  breakBetBuilderEventIntoBets,
  findBetBuilderByChildOutcome,
  getSameEventSelections,
  removeBetBuilderChildFromBets,
} from '../../utils/betBuilder';
import { BetslipDerivedState, deriveFromBets } from './derive';
import {
  BetslipRecord,
  cancelPendingBetsPersistence,
  clearStoredBets,
  flushPendingBetsPersistence,
  loadStoredBets,
  persistBets,
} from './persistence';

// Info messages are stored as i18n keys; consumers translate at render time.
export const BETSLIP_INFO_KEYS = {
  maxSelections: 'maxSelectionsAlreadyMade',
  maxBankers: 'maxSelectionsBanker',
  ticketCopied: 'ticketSuccessfullyCopied',
} as const;

// App-level context the store actions need but that lives in React land (auth, feature flags,
// react-query data). BetslipBridge keeps it in sync.
type BetslipAppContext = {
  isAuthenticated: boolean;
  isBetBuilderEnabled: boolean;
  globalTicketConditions?: GlobalTicketConditionsResponse;
  calculateBetBuilder?: (eventId: string, outcomeIds: string[]) => Promise<BetBuilderCalculateResponse>;
};

const INITIAL_APP_CONTEXT: BetslipAppContext = {
  isAuthenticated: false,
  isBetBuilderEnabled: false,
  globalTicketConditions: undefined,
  calculateBetBuilder: undefined,
};

const infoMessageTimers = new Map<string, ReturnType<typeof setTimeout>>();

const clearInfoMessageTimers = () => {
  infoMessageTimers.forEach((timer) => clearTimeout(timer));
  infoMessageTimers.clear();
};

export type BetslipStoreState = BetslipDerivedState & {
  bets: BetslipRecord;
  infoMessageKeys: ReadonlySet<string>;
  reofferedBettingSlipsIds: string[];
  selectedWallet: WalletType;
  appContext: BetslipAppContext;

  addBet: (bet: BetslipEvent) => boolean;
  addOrRemoveBet: (bet: BetslipEvent) => void;
  removeBet: (outcomeId: string) => void;
  removeAllBets: () => void;
  updateSingleBetOdds: (payload: ErrorMessageOddsChangePayload) => void;
  isOutcomeSelected: (outcomeId: string) => boolean;
  toggleBanker: (outcomeId: string) => void;
  resetBankers: () => void;
  acceptChanges: () => void;
  clearErrors: () => void;
  insertCopiedBetslip: (bets: BetslipEvent[]) => void;
  updateSinglesStakeAmount: (outcomeId: string, amount: string) => void;
  resetSinglesStakeAmounts: () => void;
  setSelectedWallet: (wallet: WalletType) => void;
  setTimedInfoMessage: (messageKey: string, duration?: number) => void;
  removeInfoMessage: (messageKey: string) => void;
  addReofferedBettingSlipId: (bettingSlipId: string) => void;
  removeReofferedBettingSlip: (bettingSlipId: string) => void;
  applySocketMessages: (data: WebsocketData<MessageBetType>[]) => void;
  resolveBetBuilder: (eventId: string, selections: BetBuilderSelection[]) => void;
  setAppContext: (appContext: Partial<BetslipAppContext>) => void;
};

// FIXME: improve this function
const checkIsInBetslip = (bet: BetslipEvent, message: MessageType<MessageBetType>) => {
  if (!message) return false;

  const { event, payload } = message;
  if (!payload || !event) return false;

  return (
    (event === 'OUTCOME' && payload.id === bet.outcomeId) ||
    (event === 'MARKET' && payload.id === bet.marketId) ||
    payload.id === bet.eventId
  );
};

// The only bet fields a socket message can change (betslipMessageParser output plus the
// disabled → singlesStakeAmount reset). Used to skip commits when a flush changed nothing.
const BET_MESSAGE_FIELDS = [
  'odds',
  'oddsChange',
  'outcomeChange',
  'marketChange',
  'matchChange',
  'disabled',
  'singlesStakeAmount',
] as const;

// Treats undefined and the parser's falsy defaults (OddsChangeStatus.unchanged = 0,
// disabled = false) as equivalent, since betslipMessageParser normalizes absent flags.
const isBetMessageEquivalent = (a: BetslipEvent, b: BetslipEvent) =>
  BET_MESSAGE_FIELDS.every((field) => a[field] === b[field] || (!a[field] && !b[field]));

const createInitialData = (bets: BetslipRecord) => ({
  bets,
  ...deriveFromBets(bets),
  infoMessageKeys: new Set<string>(),
  reofferedBettingSlipsIds: [] as string[],
  selectedWallet: WALLET_TYPE.MAIN as WalletType,
  appContext: INITIAL_APP_CONTEXT,
});

const useInternalBetslipStore = create<BetslipStoreState>()(
  subscribeWithSelector((set, get) => {
    // Single commit point for `bets`: recomputes all derived state in the same update (including
    // the max-banker info message) and persists. Skipping identical references keeps no-op socket
    // flushes from notifying any subscriber.
    const commitBets = (nextBets: BetslipRecord, persist: 'immediate' | 'throttled' = 'immediate') => {
      const previous = get();
      if (nextBets === previous.bets) return;

      const derived = deriveFromBets(nextBets, previous);

      let infoMessageKeys = previous.infoMessageKeys;
      if (derived.isMaxBankerCountReached !== infoMessageKeys.has(BETSLIP_INFO_KEYS.maxBankers)) {
        const nextKeys = new Set(infoMessageKeys);
        if (derived.isMaxBankerCountReached) {
          nextKeys.add(BETSLIP_INFO_KEYS.maxBankers);
        } else {
          nextKeys.delete(BETSLIP_INFO_KEYS.maxBankers);
        }
        infoMessageKeys = nextKeys;
      }

      set({ bets: nextBets, ...derived, infoMessageKeys });
      persistBets(nextBets, persist);
    };

    const mapBets = (mapBet: (bet: BetslipEvent) => BetslipEvent) => {
      const nextBets: BetslipRecord = {};

      Object.entries(get().bets).forEach(([key, bet]) => {
        nextBets[key] = mapBet(bet);
      });

      return nextBets;
    };

    return {
      ...createInitialData(loadStoredBets()),

      addBet: (newBet) => {
        const { betslipTicketType, betCount, selectedWallet, appContext, setTimedInfoMessage } = get();
        const betType = newBet.isLive ? BETSLIP_TICKET_TYPE.IN_PLAY : BETSLIP_TICKET_TYPE.PRE_MATCH;

        // We check if the ticket is already a mixed ticket, or if by adding a new bet of a different type it would become a mixed ticket
        const isMixTicket = betslipTicketType === BETSLIP_TICKET_TYPE.MIXED || betType !== betslipTicketType;

        const activeWalletConditions = selectedWallet ? appContext.globalTicketConditions?.[selectedWallet] : undefined;
        const maxSelections = isMixTicket
          ? activeWalletConditions?.[BETSLIP_TICKET_TYPE.MIXED].maxSelections
          : activeWalletConditions?.[betType]?.maxSelections;
        const canAddBet = !maxSelections || betCount < maxSelections;

        if (!canAddBet) {
          setTimedInfoMessage(BETSLIP_INFO_KEYS.maxSelections, 5000);
          return false;
        }

        commitBets({ ...get().bets, [newBet.outcomeId]: { ...newBet, banker: false } });
        return true;
      },

      addOrRemoveBet: (bet) => {
        const { isOutcomeSelected, removeBet, addBet, resolveBetBuilder } = get();

        if (isOutcomeSelected(bet.outcomeId)) {
          removeBet(bet.outcomeId);
          return;
        }

        if (!addBet(bet)) {
          return;
        }

        const sameEventSelections = getSameEventSelections(Object.values(get().bets), bet.eventId);
        resolveBetBuilder(bet.eventId, sameEventSelections);
      },

      removeBet: (outcomeId) => {
        const { bets, removeInfoMessage, resolveBetBuilder } = get();
        const betBuilderBet = findBetBuilderByChildOutcome(Object.values(bets), outcomeId);

        if (betBuilderBet?.betBuilder) {
          const eventToResolve = betBuilderBet.betBuilder.eventId;
          const { nextBets, remainingSelections } = removeBetBuilderChildFromBets(bets, outcomeId);

          commitBets(nextBets);
          removeInfoMessage(BETSLIP_INFO_KEYS.maxSelections);

          if (remainingSelections.length > 1) {
            resolveBetBuilder(eventToResolve, remainingSelections);
          }

          return;
        }

        const betToRemove = bets[outcomeId];

        if (!betToRemove) {
          return;
        }

        const eventToRemove = betToRemove.eventId;
        const nextBets = omit(bets, outcomeId);
        const remainingSelections = getSameEventSelections(Object.values(nextBets), eventToRemove);

        commitBets(nextBets);
        removeInfoMessage(BETSLIP_INFO_KEYS.maxSelections);

        if (remainingSelections.length >= 2) {
          resolveBetBuilder(eventToRemove, remainingSelections);
        }
      },

      removeAllBets: () => {
        commitBets({});
        clearStoredBets();
        get().removeInfoMessage(BETSLIP_INFO_KEYS.maxSelections);
      },

      updateSingleBetOdds: ({ outcomeId, odds, oddsChange }) => {
        const { bets } = get();
        const bet = bets[outcomeId];

        if (!bet) {
          return;
        }

        commitBets({
          ...bets,
          [outcomeId]: { ...bet, odds: odds.toString(), oddsChange: OddsChangeStatus[oddsChange] },
        });
      },

      isOutcomeSelected: (outcomeId) => get().selectedOutcomeKeys.has(outcomeId),

      toggleBanker: (outcomeId) => {
        const { bets } = get();
        const bet = bets[outcomeId];

        if (!bet) {
          return;
        }

        commitBets({
          ...bets,
          [outcomeId]: { ...bet, banker: !bet.banker },
        });
      },

      resetBankers: () => {
        commitBets(mapBets((bet) => ({ ...bet, banker: false })));
      },

      acceptChanges: () => {
        commitBets(
          mapBets((bet) => ({
            ...bet,
            oddsChange: 0,
            outcomeChange: 0,
            marketChange: 0,
            matchChange: 0,
          }))
        );
      },

      clearErrors: () => {
        // Errors are derived from the bets' change flags, so accepting the changes clears them.
        get().acceptChanges();
      },

      insertCopiedBetslip: (copiedBets) => {
        const { removeAllBets, setTimedInfoMessage } = get();

        removeAllBets();

        const nextBets: BetslipRecord = {};
        copiedBets.forEach((bet) => {
          nextBets[bet.outcomeId] = bet;
        });

        commitBets(nextBets);
        setTimedInfoMessage(BETSLIP_INFO_KEYS.ticketCopied);
      },

      updateSinglesStakeAmount: (outcomeId, amount) => {
        const { bets } = get();
        const bet = bets[outcomeId];

        if (!bet) {
          return;
        }

        commitBets({
          ...bets,
          [outcomeId]: { ...bet, singlesStakeAmount: amount },
        });
      },

      resetSinglesStakeAmounts: () => {
        commitBets(mapBets((bet) => ({ ...bet, singlesStakeAmount: undefined })));
      },

      setSelectedWallet: (wallet) => {
        set({ selectedWallet: wallet });
      },

      setTimedInfoMessage: (messageKey, duration = 3000) => {
        set((state) => ({ infoMessageKeys: new Set(state.infoMessageKeys).add(messageKey) }));

        const previousTimer = infoMessageTimers.get(messageKey);
        if (previousTimer) {
          clearTimeout(previousTimer);
        }

        const timer = setTimeout(() => {
          infoMessageTimers.delete(messageKey);
          get().removeInfoMessage(messageKey);
        }, duration);
        infoMessageTimers.set(messageKey, timer);
      },

      removeInfoMessage: (messageKey) => {
        set((state) => {
          if (!state.infoMessageKeys.has(messageKey)) {
            return state;
          }

          const infoMessageKeys = new Set(state.infoMessageKeys);
          infoMessageKeys.delete(messageKey);
          return { infoMessageKeys };
        });
      },

      addReofferedBettingSlipId: (bettingSlipId) => {
        set((state) => ({ reofferedBettingSlipsIds: [...state.reofferedBettingSlipsIds, bettingSlipId] }));
      },

      removeReofferedBettingSlip: (bettingSlipId) => {
        set((state) => ({
          reofferedBettingSlipsIds: state.reofferedBettingSlipsIds.filter((id) => id !== bettingSlipId),
        }));
      },

      applySocketMessages: (data) => {
        const { bets } = get();
        let nextBets: BetslipRecord | null = null;

        Object.entries(bets).forEach(([outcomeId, bet]) => {
          let updatedBet = bet;

          data.forEach((message) => {
            const socketMessage = message[bet.eventId];

            if (!checkIsInBetslip(bet, socketMessage)) {
              return;
            }

            updatedBet = { ...updatedBet, ...betslipMessageParser(updatedBet, socketMessage) };

            // if disabled set singlesStakeAmount to undefined
            if (updatedBet.disabled) {
              updatedBet = { ...updatedBet, singlesStakeAmount: undefined };
            }
          });

          if (updatedBet !== bet && !isBetMessageEquivalent(bet, updatedBet)) {
            nextBets = nextBets ?? { ...bets };
            nextBets[outcomeId] = updatedBet;
          }
        });

        // No matching message changed anything → no commit, no subscriber notifications.
        if (nextBets) {
          commitBets(nextBets, 'throttled');
        }
      },

      resolveBetBuilder: (eventId, selections) => {
        const { appContext } = get();

        if (
          !appContext.isBetBuilderEnabled ||
          !appContext.isAuthenticated ||
          !appContext.calculateBetBuilder ||
          selections.length < 2
        ) {
          return;
        }

        const requestedOutcomeIds = selections.map((selection) => selection.outcomeId);

        void appContext
          .calculateBetBuilder(eventId, requestedOutcomeIds)
          .then((response: BetBuilderCalculateResponse) => {
            const { bets } = get();

            if (response.combo_odds === null) {
              if (areSameEventSelectionsCurrent(bets, eventId, requestedOutcomeIds)) {
                commitBets(breakBetBuilderEventIntoBets(bets, eventId));
              }
              return;
            }

            commitBets(applyBetBuilderResponseToBets(bets, eventId, response, requestedOutcomeIds));
          })
          .catch(() => {
            const { bets } = get();

            if (areSameEventSelectionsCurrent(bets, eventId, requestedOutcomeIds)) {
              commitBets(breakBetBuilderEventIntoBets(bets, eventId));
            }
          });
      },

      setAppContext: (appContext) => {
        set((state) => ({ appContext: { ...state.appContext, ...appContext } }));
      },
    };
  })
);

// Narrow public interface: consumers may select state or invoke commands via getBetslipState,
// but cannot bypass commitBets with Zustand's raw setState.
export const useBetslipStore = <T>(selector: (state: BetslipStoreState) => T): T => useInternalBetslipStore(selector);

export const getBetslipState = () => useInternalBetslipStore.getState();

export const subscribeToBetslip = (listener: (state: BetslipStoreState, previousState: BetslipStoreState) => void) =>
  useInternalBetslipStore.subscribe(listener);

export const subscribeToBetslipBets = (listener: (bets: BetslipRecord, previousBets: BetslipRecord) => void) =>
  useInternalBetslipStore.subscribe((state) => state.bets, listener);

export const teardownBetslipRuntime = () => {
  clearInfoMessageTimers();
  flushPendingBetsPersistence();
  getBetslipState().setAppContext(INITIAL_APP_CONTEXT);
};

// Test helper: restores a pristine store (empty betslip, default wallet, no messages) while
// keeping action implementations intact.
export const resetBetslipStore = () => {
  clearInfoMessageTimers();
  cancelPendingBetsPersistence();
  clearStoredBets();
  useInternalBetslipStore.setState(createInitialData({}));
};
